import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { getEffectiveRoles, isUserProfileComplete } from '@/features/accounts/utils'
import { getJwtExpiryMs } from '@/lib/jwt-utils'
import type {
  User,
  UserDetails,
  UserRole,
  UserProfile,
  SocialAccount,
  SchoolAssignment,
} from '@/features/accounts/types'

/** localStorage key of the persisted auth blob (also watched for cross-tab sign-out). */
export const AUTH_STORAGE_KEY = 'auth-storage'

interface AuthState {
  user: UserDetails | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  role: UserRole | null
  /** null = unknown / not yet cached */
  profileComplete?: boolean | null
  /** Incremented when user switches, to invalidate cached profile status */
  profileCacheVersion?: number
  /** Access token lifetime in seconds from the last token response (OAuth hash or login API). */
  accessTokenExpiresInSeconds?: number | null
  /**
   * True after this tab signed out (not persisted). The page the previous user was on must
   * not become the next user's post-login destination.
   */
  signedOut: boolean
  /**
   * Bumped whenever the signed-in session is replaced or ended (sign-in, sign-out, account
   * switch; not persisted, not changed by token refresh). Async work started for one session
   * compares it before writing results so a late response cannot land in the next session.
   */
  sessionId: number

  setAuth: (tokens: {
    access_token: string
    refresh_token: string
    token_type: 'Bearer'
    expires_in: number
    user: User
  }) => void
  setTokens: (args: { access: string; refresh: string; expiresIn?: number }) => void
  setUser: (user: UserDetails | null) => void
  setProfileComplete: (complete: boolean) => void
  clearAuth: () => void
  updateAccessToken: (token: string) => void
  applyTokenRefresh: (args: {
    access: string
    refresh?: string | null
    expiresIn?: number | null
  }) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      role: null,
      profileComplete: null,
      profileCacheVersion: 0,
      accessTokenExpiresInSeconds: null,
      signedOut: false,
      sessionId: 0,

      setAuth: tokens => {
        // Create a minimal profile from User data if profile doesn't exist
        const userWithProfile = {
          ...tokens.user,
          is_super_admin: Boolean(tokens.user.is_super_admin),
          profile:
            (tokens.user as Partial<UserDetails>).profile ??
            ({
              first_name: tokens.user.first_name || '',
              last_name: tokens.user.last_name || '',
              full_name: `${tokens.user.first_name || ''} ${tokens.user.last_name || ''}`.trim(),
              middle_name: null,
              phone_number: null,
              avatar_key: null,
            } satisfies UserProfile),
          school_assignments:
            (tokens.user as Partial<UserDetails>).school_assignments ?? ([] as SchoolAssignment[]),
          social_accounts:
            (tokens.user as Partial<UserDetails>).social_accounts ?? ([] as SocialAccount[]),
        } as UserDetails

        const expiresIn =
          typeof tokens.expires_in === 'number' &&
          Number.isFinite(tokens.expires_in) &&
          tokens.expires_in > 0
            ? tokens.expires_in
            : null

        set({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          user: userWithProfile,
          isAuthenticated: true,
          role: tokens.user.role,
          accessTokenExpiresInSeconds: expiresIn,
          signedOut: false,
          sessionId: get().sessionId + 1,
        })
      },

      setTokens: ({ access, refresh, expiresIn }) =>
        set(state => ({
          sessionId: state.sessionId + 1,
          accessToken: access,
          refreshToken: refresh,
          isAuthenticated: true,
          signedOut: false,
          accessTokenExpiresInSeconds:
            expiresIn !== undefined && Number.isFinite(expiresIn) && expiresIn > 0
              ? expiresIn
              : null,
        })),

      setUser: user => {
        set(state => ({
          user,
          role: user?.role ?? null,
          profileCacheVersion: (state.profileCacheVersion || 0) + 1,
        }))

        if (user) {
          set({ profileComplete: isUserProfileComplete(user) })
        } else {
          set({ profileComplete: null })
        }
      },

      setProfileComplete: complete => set({ profileComplete: complete }),

      clearAuth: () => {
        set(state => ({
          sessionId: state.sessionId + 1,
          isLoading: false,
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          role: null,
          profileComplete: null,
          profileCacheVersion: 0,
          accessTokenExpiresInSeconds: null,
          signedOut: true,
        }))
      },

      updateAccessToken: token => set({ accessToken: token }),

      applyTokenRefresh: ({ access, refresh, expiresIn }) =>
        set(state => {
          let accessTokenExpiresInSeconds = state.accessTokenExpiresInSeconds ?? null
          if (
            expiresIn !== undefined &&
            expiresIn !== null &&
            Number.isFinite(expiresIn) &&
            expiresIn > 0
          ) {
            accessTokenExpiresInSeconds = expiresIn
          } else {
            const expMs = getJwtExpiryMs(access)
            if (expMs) {
              accessTokenExpiresInSeconds = Math.max(1, Math.round((expMs - Date.now()) / 1000))
            }
          }
          return {
            accessToken: access,
            refreshToken: refresh !== undefined && refresh !== null ? refresh : state.refreshToken,
            accessTokenExpiresInSeconds,
          }
        }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      // Only persist tokens — user data is refetched on startup to stay fresh
      partialize: state => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        profileComplete: state.profileComplete,
        accessTokenExpiresInSeconds: state.accessTokenExpiresInSeconds,
      }),
      // Restore user data on startup when tokens exist but user data was not persisted
      onRehydrateStorage: () => state => {
        if (state && state.accessToken && state.isAuthenticated && !state.user) {
          state.isLoading = true
          const sessionId = state.sessionId ?? 0
          // Results of this restore belong to the session that started it.
          const sessionUnchanged = () => useAuthStore.getState().sessionId === sessionId
          // A stored access token is usually stale after a long absence. Refresh it first so the
          // first profile request is authenticated instead of failing with a 401 and being retried.
          const refreshIfExpired = async () => {
            const expMs = state.accessToken ? getJwtExpiryMs(state.accessToken) : null
            if (expMs === null || expMs - Date.now() > 5_000 || !state.refreshToken) return
            try {
              const { performTokenRefresh } = await import('@/lib/auth-token-refresh')
              await performTokenRefresh()
            } catch {
              // The profile request below handles an unusable session (it signs the user out).
            }
          }
          Promise.all([import('@/features/accounts/services'), refreshIfExpired()]).then(
            ([{ profileApi }]) => {
              profileApi
                .getProfile()
                .then(profile => {
                  if (!sessionUnchanged()) return
                  useAuthStore.setState({
                    user: profile,
                    role: profile.role,
                    profileComplete: isUserProfileComplete(profile),
                    profileCacheVersion: (state?.profileCacheVersion || 0) + 1,
                    isLoading: false,
                  })
                })
                .catch(() => {
                  if (!sessionUnchanged()) return
                  useAuthStore.getState().clearAuth()
                })
            },
          )
        }
      },
    },
  ),
)

export const selectUser = (s: AuthState) => s.user
export const selectRole = (s: AuthState) => s.role ?? s.user?.role ?? null
export const selectIsSuperAdmin = (s: AuthState) => Boolean(s.user?.is_super_admin)
/**
 * All active roles, primary first; `roles` when the API sent it, else the legacy `role`.
 * Returns a new array, so subscribe with `useAuthStore(useShallow(selectRoles))`.
 */
export const selectRoles = (s: AuthState): UserRole[] =>
  getEffectiveRoles({ role: s.user?.role ?? s.role, roles: s.user?.roles })
export const selectAccessToken = (s: AuthState) => s.accessToken
// Require token + flag + user (or loading while user is being restored)
export const selectIsAuthenticated = (s: AuthState) =>
  s.isAuthenticated && Boolean(s.accessToken) && (Boolean(s.user) || s.isLoading)

export type { UserRole } from '@/features/accounts/types'

/**
 * Keep tabs in sync through the persisted auth blob: signing out in one tab signs out the
 * others, and a refresh-token rotation done in another tab is adopted here (the old token
 * is no longer valid server-side).
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.storageArea !== localStorage) return
    if (event.key !== null && event.key !== AUTH_STORAGE_KEY) return

    let persisted: { accessToken?: string | null; isAuthenticated?: boolean } | null = null
    try {
      persisted = event.newValue ? JSON.parse(event.newValue)?.state : null
    } catch {
      persisted = null
    }

    if (!persisted?.accessToken || !persisted.isAuthenticated) {
      if (useAuthStore.getState().isAuthenticated) useAuthStore.getState().clearAuth()
      return
    }
    if (persisted.accessToken !== useAuthStore.getState().accessToken) {
      void useAuthStore.persist.rehydrate()
    }
  })
}
